import React from 'react';
import Immutable from 'immutable';
import {
	styled,
	ParentBoundComponent,
	Button,
	Form,
	FormItem,
	Cross,
	DateInput,
	Centerer,
} from 'ps-gui';

import {
	getFormItemLabel,
	getPhrase,
} from '../../data/phrases';
import LabelAdminModel from './labeladminmodel';
import {
	formatHtml,
	getGlobalContextChangeChecker
} from '../utils';
import { withGlobalContext } from '../../data/globalcontext';
import Message from '../../blocks/message';
import { getLockMessage } from './labelutils';


const Wrapper = styled.div`
	.ps-form {
		width: 480px;
	}
`;

const Content = styled.div`
	margin-top: 24px;
`;

const FormWrapper = styled.div`
	max-width: 700px;
	.ps-formItem {
		margin-bottom: 24px;
	}
`;

const RowCountEtc = styled.div`
	margin-bottom: 24px;
`;

const RowCount = styled.div`
	display: inline-block;
	color: ${props => props.theme.disabledTextColor};
	font-size: 20px;
	margin-right: 40px;
	.label {
		font-weight: 400;
		margin-right: 5px;
	}
	.value {
		font-weight: 900;
	}
`;

const TabText = styled.span`

	.icon {
		position: relative;
		top: -1px;
	}

	.icon.left {
		margin-right: 8px;
	}

	.icon[data-name=close] {
		margin-left: 16px;
		* {
			fill: #4FC098;
		}
	}
`;

const QuickFilterHeader = styled.div`
	font-size: 14px;
	font-weight: 500;
	color: ${props => props.theme.disabledTextColor};
	margin-bottom: 8px;
`;

const QuickFilter = styled.div`
	margin-bottom: 24px;
	.checkButton {
		margin-right: 16px;
	}
`;

const DialogLabel = styled.div`
	position: relative;
	.createdWhen {
		color: #575757CC;
		position: absolute;
		right: 0px;
		top: 0px;
		font-weight: 300;
		font-size: 14px;
		line-height: 30px;
	}

	.type {
		padding-right: 80px;
		font-weight: 400;
		font-size: 16px;
		line-height: 24px;
	}

	.name {
		font-weight: 500;
		font-size: 16px;
		line-height: 30px;
		color: #575757;
		margin-right: 16px;
		display: inline-block;
	}
`;

const EMPTY_MAP = Immutable.Map({});
const EMPTY_LIST = Immutable.List([]);


const recAttrs = [
	'isrc',
	'title',
	'subtitle',
	'producer',
	'label',
	'recordingTypeId',
	'genreId',
	'productionYear',
	'releaseYear',
	'studio',
	'masterNumber',
	'albumCatalogNumber',
	'trackNumber',
	'length',
	'language',
	'recordingRegistrationDate',
	'recordingCountryId',
	'album',
	'mainArtist',
];

const RecordingAttribute = styled.div`
	padding: 2px 8px;
	white-space: nowrap;

	.label {
		display: inline-block;
		min-width: 220px;
		padding-right: 8px;
		color: ${props => props.theme.disabledTextColor};
	}

	.value {
		display: inline-block;
		font-weight: 500;
		padding-right: 8px;
		min-width: 220px;
		white-space: normal;
		max-width: calc( 100vw - 440px );
	}

	.subvalue {
		display: inline-block;
		font-size: 90%;
		margin-left: 8px;
	}

	cursor: default;
	&:hover {
		background: ${props => props.theme.tableRowSelectedBackground};
	}
`;

const SubHeader = styled.div`
	text-align: center;
	margin: 8px 0px;
	font-weight: 500;
	color: ${props => props.theme.disabledTextColor};
`;

class LabelAdminModule extends ParentBoundComponent {

	constructor() {
		super();
		this.checkGlobalContextChange = getGlobalContextChangeChecker(this);
	}

	async reinitialize() {
		let model = new LabelAdminModel();
		model = await model.initialize();
		this.setModel(model);
	}

	getModel() {
		return new LabelAdminModel(this.getData());
	}

	setModel(model) {
		this.setData(model.getData());
	}

	async componentDidMount() {
		this.checkGlobalContextChange();
	}

	componentDidUpdate(prevProps) {
		this.checkGlobalContextChange(prevProps);
	}

	renderClientMessage(lockedFromDate, lockedToDate) {
		if (!lockedFromDate) {
			return (
				<UnlockedMessage>
					{getPhrase('labelModuleIsUnlocked')}
					{
						lockedToDate && (
							<div className='warning'>{getPhrase('labelModuleIsUnlockedWithLockedToDateSet')}</div>
						)
					}
				</UnlockedMessage>
			);
		}
		const message = getLockMessage(lockedFromDate, lockedToDate);
		if (message) {
			return (
				<Message
				>
					<span dangerouslySetInnerHTML={{ __html: formatHtml(message) }} />
				</Message>
			);
		} else {
			return (
				<UnlockedMessage>{getPhrase('labelModuleIsUnlocked')}</UnlockedMessage>
			);
		}
	}

	render() {
		const data = this.getData();
		if (!data.get('initialized')) {
			return null;
		}
		const lockedFromDate = data.get('lockedFromDate');
		const lockedToDate = data.get('lockedToDate');
		return (
			<Wrapper>
				<Centerer>
					<Form>
						<Cross>
							<FormItem label={getFormItemLabel('labelModuleLockedFromDate')}>
								<DateInput
									value={lockedFromDate}
									onChange={value => {
										this.setModel(this.getModel().setLockedFromDate(value));
									}}
									withTime={false}
								/>
							</FormItem>
							<FormItem label={getFormItemLabel('labelModuleLockedToDate')}>
								<DateInput
									value={lockedToDate}
									onChange={value => {
										this.setModel(this.getModel().setLockedToDate(value));
									}}
									withTime={false}
								/>
							</FormItem>
						</Cross>
						<Cross justifyContent="right">
							<Button
								text={getPhrase('save')}
								disabled={lockedToDate && !lockedFromDate}
								onAction={async () => {
									this.setModel(await this.getModel().save());
								}}
							/>
						</Cross>
					</Form>
					{this.renderClientMessage(lockedFromDate, lockedToDate)}
				</Centerer>
			</Wrapper>
		);
	}

}

const UnlockedMessage = styled.div`
	margin: 16px auto;
	display: table;
	color: ${props => props.theme.okColor};
	text-align: center;
	.warning {
		color: ${props => props.theme.errorColor};
		margin: 8px;
	}
`;


export default withGlobalContext(LabelAdminModule);
